import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../api/client";
import { useAIFlags } from "./AIAssistPanel";
import { useCustomer } from "../context/CustomerContext";
import { getAIScreenData } from "../utils/aiScreenContext";

// Map the current route to an action tool-scope for the AI (which change tools
// the assistant may propose). Longest matching prefix wins.
function scopeFor(path: string): string {
  if (
    path.startsWith("/m365") ||
    path.startsWith("/entra-users") ||
    path.startsWith("/intune")
  )
    return "m365";
  if (path.startsWith("/meraki-cloud")) return "meraki";
  if (path.startsWith("/active-directory") || path.startsWith("/tools")) return "identity";
  if (path.startsWith("/tickets")) return "ticket";
  if (path.startsWith("/alerts")) return "alert";
  if (
    path.startsWith("/devices") ||
    path.startsWith("/network") ||
    path.startsWith("/patch") ||
    path.startsWith("/software") ||
    path.startsWith("/remediation") ||
    path.startsWith("/scripts") ||
    path.startsWith("/security") ||
    path.startsWith("/monitoring/reboots") ||
    path.startsWith("/monitoring/maintenance")
  )
    return "device";
  // Everything else (dashboard, customers, reports, audit, users, PSA,
  // integrations, backup, settings, …) gets the FULL tool set so the
  // assistant can do anything the screen — or the platform — allows.
  return "general";
}

type ProposedAction = {
  id: number;
  action_type: string;
  params: Record<string, unknown>;
  risk_tier: string;
  status: string;
  proposed_reason?: string | null;
  consequence?: string | null;
  disruptive?: boolean;
  error_message?: string | null;
};

// Screen awareness: map a route prefix to a friendly label + example prompts.
// Longest matching prefix wins.
// Every authenticated route → a friendly label + a few relevant starter
// prompts. Longest-prefix match wins (see screenFor), so specific children
// (e.g. /monitoring/certificates) resolve before their parent (/monitoring).
// Keep this in sync with App.tsx routes so the assistant recognizes EVERY
// screen — anything not listed still works via the dynamic "Suggest" button.
const SCREENS: { prefix: string; label: string; examples: string[] }[] = [
  { prefix: "/customers", label: "Customers", examples: ["How do I onboard a new customer?", "Run a prospect network scan for a customer", "Difference between the Assessment and Inventory reports?"] },
  { prefix: "/devices", label: "All Devices", examples: ["Which devices are offline or unhealthy?", "Reboot the devices that are behind on patches", "How do I bulk-reinstall the agent?"] },
  { prefix: "/network-discovery", label: "Network Discovery", examples: ["What did the last discovery scan find?", "Deploy the agent to a discovered device", "Which discovered devices aren't managed yet?"] },
  { prefix: "/network", label: "Network Devices", examples: ["Reboot a switch safely", "Push a VLAN change to a switch port", "Open an SSH console to a firewall"] },
  { prefix: "/alerts", label: "Alerts", examples: ["Which alerts are most urgent right now?", "Summarize the open alerts and likely root causes", "Create a ticket from an alert"] },
  { prefix: "/monitoring/maintenance", label: "Maintenance Windows", examples: ["Put a device into maintenance mode", "How do maintenance windows suppress alerts?"] },
  { prefix: "/monitoring/reboots", label: "Reboot Schedules", examples: ["Schedule a reboot for a device tonight", "Which devices have a pending scheduled reboot?"] },
  { prefix: "/monitoring/certificates", label: "Certificates", examples: ["Which certificates are expiring soon?", "Renew an expiring cert on a device", "Why is a certificate showing as expired?"] },
  { prefix: "/monitoring/urls", label: "Website / URL Monitoring", examples: ["Add a website to monitor for uptime + TLS expiry", "Which monitored sites are down?"] },
  { prefix: "/monitoring/settings", label: "Monitoring & Alerts — Settings", examples: ["Set an offline threshold", "Turn off alert emails", "Set per-customer alert thresholds"] },
  { prefix: "/monitoring", label: "Monitoring & Alerts", examples: ["What does this screen monitor?", "How do maintenance windows work?"] },
  { prefix: "/onboarding", label: "Onboarding", examples: ["Walk me through onboarding a new customer", "What onboarding steps are still incomplete?"] },
  { prefix: "/tickets", label: "Tickets", examples: ["Summarize the open tickets", "Draft a reply for a ticket", "Set a ticket's priority to urgent"] },
  { prefix: "/calendar", label: "Calendar", examples: ["What's scheduled this week?", "How do scheduled jobs appear here?"] },
  { prefix: "/active-directory", label: "Active Directory", examples: ["Unlock the account for jsmith", "Reset a user's AD password", "Disable a departing employee's account"] },
  { prefix: "/tools/dns", label: "DNS Tools", examples: ["Add an A record 'vpn' → 10.0.0.5 in example.com", "Delete a stale DNS record", "Create a reverse lookup zone"] },
  { prefix: "/tools/dhcp", label: "DHCP Tools", examples: ["Reserve 10.0.0.50 for a MAC in the main scope", "Set DNS servers (option 6) on a scope", "Create a new DHCP scope"] },
  { prefix: "/tools/gpo", label: "Group Policy", examples: ["Create a GPO called 'Kiosk Lockdown'", "Configure Automatic Updates via a GPO", "Link a GPO to an OU"] },
  { prefix: "/tools", label: "Identity & Tools", examples: ["What can I manage from here?", "Make an AD / DNS / DHCP / GPO change"] },
  { prefix: "/remediation", label: "Self-Healing", examples: ["Add a rule to auto-restart a service", "Which self-healing rules fired recently?", "What should I auto-remediate?"] },
  { prefix: "/patch", label: "Patch Compliance", examples: ["Which devices are behind on patches?", "Install pending patches on the behind devices", "Why did a patch fail?"] },
  // No vendor name: this one screen serves every controller, and a firewall
  // labelled with a wireless vendor's name reads as the wrong page.
  { prefix: "/meraki-cloud", label: "Cloud Controllers", examples: ["Reboot the access point named Lobby", "Disable the Guest wireless network", "Which subscriptions expire soon?"] },
  { prefix: "/m365-tenants", label: "Microsoft 365 Tenants", examples: ["Why is a tenant showing no data?", "How do I connect a new M365 tenant?"] },
  { prefix: "/m365", label: "Microsoft 365", examples: ["Offboard a departing user", "Assign an M365 license to a user", "Set an out-of-office reply"] },
  { prefix: "/entra-users", label: "Entra Users", examples: ["Disable a user's sign-in", "Revoke a user's active sessions", "Reset a user's MFA methods"] },
  { prefix: "/intune", label: "Intune", examples: ["Sync an Intune device", "Remotely lock a lost device", "Which Intune devices are non-compliant?"] },
  // ── Cloud ──────────────────────────────────────────────────────────────
  // REPORTED: "the AI assist may need updating under cloud, especially with
  // compute... Networking isn't showing me much either." Two causes: only one
  // cloud route was listed here at all, and its examples named specific
  // vendors ("Start a stopped Azure VM", "Snapshot an EBS volume"), which the
  // product does not do - capabilities are DETECTED from the connector, so the
  // prompts have to read the same way whichever cloud is attached.
  // The screens now also publish their rows (usePublishScreenData), so
  // "these instances" means the ones actually on screen.
  { prefix: "/cloud/compute", label: "Cloud Compute", examples: ["Which instances are running, and which are idle enough to stop?", "Snapshot the disks on this connection before I make changes", "Stop the instances that nobody is using out of hours"] },
  { prefix: "/cloud/network", label: "Cloud Networking", examples: ["Do any of these networks have overlapping address ranges?", "Summarise the address space in use and what is left", "Which networks look unused?"] },
  { prefix: "/cloud/identity", label: "Cloud Identity", examples: ["Who here has administrator rights?", "Which accounts are suspended but still licensed?", "Find accounts that look like they belong to someone who left"] },
  { prefix: "/cloud/storage", label: "Cloud Storage", examples: ["Is any storage publicly reachable?", "Which buckets or shares have no retention set?", "Summarise what is stored here and roughly what it costs"] },
  { prefix: "/cloud-storage", label: "Cloud Storage", examples: ["Is any storage publicly reachable?", "Which buckets or shares have no retention set?", "Summarise what is stored here"] },
  { prefix: "/cloud", label: "Cloud Connectors", examples: ["Which clouds are connected, and what can AeroGauge do with each?", "Why is a connector failing its test?", "How do I connect another cloud?"] },
  { prefix: "/scripts", label: "Scripts", examples: ["Generate a PowerShell script to clear temp files", "Run a stored script on a device", "What does a script do?"] },
  { prefix: "/software", label: "Software", examples: ["Install an app via winget on a device", "Which devices are missing a required app?", "Upgrade an outdated app fleet-wide"] },
  { prefix: "/security", label: "Security", examples: ["Which devices have no EDR/AV?", "How do I issue an API token?", "What are the biggest security gaps right now?"] },
  { prefix: "/reports", label: "Reports", examples: ["Which report should I use for a QBR?", "Build a report of offline devices per customer", "Schedule a report by email"] },
  { prefix: "/audit", label: "Audit Log", examples: ["Who changed settings in the last 24 hours?", "Show all actions on a specific device", "Is everything being logged?"] },
  { prefix: "/roles-access", label: "Roles & access", examples: ["What does turning off a feature for a role actually do?", "Why can't I change the Admin column?"] },
  { prefix: "/users", label: "Users & Access", examples: ["What can a technician role do vs an admin?", "How do I add a new AeroGauge user?"] },
  { prefix: "/backup", label: "Backup & Restore", examples: ["Back up to a network share", "How do scheduled backups work?", "How do I restore?"] },
  { prefix: "/settings", label: "Settings", examples: ["What should I configure first?", "How do agent builds get here?", "Set the alert email address"] },
  { prefix: "/ai-actions", label: "AI Actions", examples: ["What's waiting for my approval?", "Explain a proposed action's risk"] },
  { prefix: "/ai-compliance", label: "Compliance", examples: ["Run a HIPAA assessment for a customer", "Which controls are failing?", "Generate a framework report"] },
  { prefix: "/ai-settings", label: "AI Settings", examples: ["What do these AI toggles do?", "How do I let the AI take actions?"] },
  { prefix: "/ai-terminal", label: "AI Terminal", examples: ["What can I do from the AI terminal?"] },
  { prefix: "/psa/contracts", label: "Contracts", examples: ["Which contracts are up for renewal?", "How is a contract billed?"] },
  { prefix: "/psa/invoices", label: "Invoices", examples: ["Which invoices are overdue?", "Generate this month's invoices"] },
  { prefix: "/psa/pipeline", label: "Sales Pipeline", examples: ["What's in the pipeline this quarter?", "Which deals are stalled?"] },
  { prefix: "/psa/rate-cards", label: "Rate Cards", examples: ["How do rate cards apply to billing?"] },
  { prefix: "/psa/ar-aging", label: "AR Aging", examples: ["Who owes us money past 60 days?", "Summarize AR aging"] },
  { prefix: "/psa-integrations", label: "PSA Integrations", examples: ["How do I connect an external PSA?"] },
  // No vendor name in the label OR the prompt: this screen serves whichever
  // controller integration is configured, and the sweep behind it also covers
  // hand-entered licences from any vendor.
  { prefix: "/meraki-licenses", label: "Licence renewals", examples: ["Which licences expire soon?"] },
  { prefix: "/licensing", label: "Licensing", examples: ["What's my AeroGauge license status?", "How many device seats are used?"] },
  { prefix: "/license", label: "License", examples: ["What's my license status?"] },
  { prefix: "/my-dashboards", label: "My Dashboards", examples: ["Build a custom dashboard widget", "What can I put on a dashboard?"] },
  { prefix: "/helpdesk/email-intake", label: "Email intake", examples: ["How does email-to-ticket work?", "Why didn't an email create a ticket?"] },
  { prefix: "/helpdesk/settings", label: "HelpDesk Settings", examples: ["How is SLA active time measured?", "What happens when a ticket breaches SLA?"] },
  { prefix: "/helpdesk/setup", label: "Mail Setup", examples: ["What DNS records does helpdesk mail need?", "Why isn't inbound mail arriving?"] },
  { prefix: "/inbound-channels", label: "Inbound Channels", examples: ["How do I add an inbound alert channel?", "What formats can I ingest?"] },
  { prefix: "/integrations/webhooks", label: "Webhooks", examples: ["Add an outbound webhook", "What events can fire a webhook?"] },
  { prefix: "/integrations", label: "Integrations", examples: ["Which integrations are available?", "Connect GoToAssist / Meraki / M365"] },
  { prefix: "/mobile", label: "Mobile", examples: ["What can I do from the mobile view?"] },
  // THESE FOUR WERE FALLING THROUGH TO "/" AND BEING TOLD THEY WERE THE
  // DASHBOARD. `screenFor` takes the longest matching prefix, and "/" matches
  // everything - so a route with no entry of its own does not get the honest
  // "this screen" fallback, it gets confidently mislabelled as the Dashboard
  // along with the Dashboard's starter prompts. That is worse than nothing,
  // and it mattered more once these screens started publishing their rows:
  // the assistant would have had the email-intake log in hand while being
  // told it was looking at fleet health.
  //
  // Found by diffing the route table in App.tsx against these prefixes;
  // tests/test_ai_assist_screens.py now does that diff.
  { prefix: "/device-stability", label: "Device Stability", examples: ["Which devices flap in and out the most?", "Is a device's instability the ISP or the machine?", "Export the flapping report for a customer"] },
  { prefix: "/email-intake", label: "Email intake", examples: ["Why didn't an email create a ticket?", "Which inbound emails were blocked and why?", "How does email-to-ticket matching work?"] },
  { prefix: "/psa/quotes", label: "Quotes", examples: ["Which quotes are still open?", "Turn an accepted quote into a contract", "What's the total value of open quotes?"] },
  { prefix: "/scheduled-documents", label: "Scheduled reports", examples: ["Which scheduled reports are failing?", "Schedule the risk assessment monthly for a customer", "What time zone does a schedule use?"] },
  // Its parent /integrations entry answered "which integrations are
  // available?" on a screen that is specifically chat destinations.
  { prefix: "/integrations/chat", label: "Chat notifications", examples: ["Send alerts to a chat channel", "Why did a test message not arrive?", "Which alerts go to which channel?"] },
  { prefix: "/help", label: "Help", examples: ["Where do I find something?", "Give me a quick tour"] },
  { prefix: "/", label: "Dashboard", examples: ["What needs my attention today?", "How many devices are offline?", "Summarize fleet health across customers"] },
];

function screenFor(path: string) {
  const m = SCREENS.filter((s) => path === s.prefix || path.startsWith(s.prefix)).sort(
    (a, b) => b.prefix.length - a.prefix.length,
  )[0];
  return m || { prefix: path, label: "this screen", examples: ["What can I do on this screen?"] };
}

// One-tap proactive prompt — asks the AI for the most useful things to do on
// the CURRENT screen, including non-obvious ideas, and (when a customer is
// selected) to propose the actionable ones as approval-gated actions.
function suggestPrompt(label: string): string {
  return (
    `I'm on the "${label}" screen. Suggest the most useful things I could do here right now, ` +
    `given what's shown — include obvious next steps AND non-obvious ideas I might not think of. ` +
    `For anything you can actually perform, propose it as an action.`
  );
}

type Msg = { role: "user" | "assistant"; content: string };

export default function AIAssist() {
  const [aiOn, setAiOn] = useState(false);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [actions, setActions] = useState<ProposedAction[]>([]);
  // Facts the assistant recorded on the last turn. Shown rather than kept
  // quiet — silent retention is what people object to when they learn of it.
  const [learned, setLearned] = useState<string[]>([]);
  const loc = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const flags = useAIFlags();
  const { currentCustomerId } = useCustomer();
  const screen = screenFor(loc.pathname);
  // Can the assistant TAKE actions here? Needs action-AI on + a specific
  // customer selected (actions are scoped to one tenant/domain).
  const canAct = currentCustomerId != null && !!flags?.ai_action_enabled;

  useEffect(() => {
    api
      .get("/api/v1/ai/enabled")
      .then((r) => setAiOn(!!r.data?.enabled))
      .catch(() => setAiOn(false));
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  // ── A CONVERSATION BELONGS TO THE SCREEN IT WAS STARTED ON ────────────────
  //
  // Reported: the panel showed the same answer on every screen instead of that
  // screen's suggestions. `messages` lived for the life of the app, so an
  // answer about the Alerts screen stayed on screen while the header said
  // "· Tickets" — stale, confusing, and it hid the per-screen starter prompts
  // (which only render in the empty state) from the moment you asked anything.
  //
  // Threads are kept PER SCREEN rather than simply cleared: navigating away
  // and back should not throw away what you were reading. A screen you have
  // not asked about yet opens empty, which is exactly when its tailored
  // suggestions should be in front of you.
  const threadsRef = useRef<Record<string, Msg[]>>({});
  const prevScreenRef = useRef<string>(screen.prefix);
  useEffect(() => {
    const prev = prevScreenRef.current;
    if (prev === screen.prefix) return;
    threadsRef.current[prev] = messages;
    prevScreenRef.current = screen.prefix;
    setMessages(threadsRef.current[screen.prefix] || []);
    // The side panels belong to the answer that produced them.
    setActions([]);
    setLearned([]);
    setErr("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen.prefix]);

  // Resume the saved thread the first time the panel is opened. Fetched on open
  // rather than on mount because the assistant renders on every page and most
  // visits never touch it.
  const [historyLoaded, setHistoryLoaded] = useState(false);
  // The previous session's thread, held aside rather than shown. See the
  // history fetch below for why it is not applied automatically.
  const [resumable, setResumable] = useState<Msg[] | null>(null);
  useEffect(() => {
    if (!open || historyLoaded || !aiOn) return;
    setHistoryLoaded(true);
    api
      .get("/api/v1/ai/assist/history")
      .then((r) => {
        const saved = (r.data?.messages || []) as Msg[];
        // OFFERED, NOT APPLIED. The saved thread carries no record of which
        // screen it came from, so dropping it into whatever screen the panel
        // is opened on reproduces exactly the bug this component was reported
        // for — a stale answer about somewhere else. Keep it to one side and
        // let the user ask for it.
        if (saved.length) setResumable(saved);
      })
      .catch(() => {});
  }, [open, historyLoaded, aiOn]);

  const clearThread = async () => {
    if (!window.confirm("Start a new conversation? The current one is kept in history.")) return;
    try {
      await api.delete("/api/v1/ai/assist/history");
    } catch {
      /* archiving failed server-side; clearing locally is still the right
         response to the click */
    }
    setMessages([]);
    setActions([]);
    setLearned([]);
  };

  const send = useCallback(
    async (text: string) => {
      const q = text.trim();
      if (!q || busy) return;
      setErr("");
      const next = [...messages, { role: "user" as const, content: q }];
      setMessages(next);
      setInput("");
      setBusy(true);
      const screenData = getAIScreenData(); // what's currently on the screen
      try {
        if (canAct) {
          // Action-capable path: the assistant can propose approval-gated
          // changes for the selected customer, scoped to this screen, aware of
          // the rows currently displayed.
          const r = await api.post("/api/v1/ai/act", {
            customer_id: currentCustomerId,
            instruction: q,
            scope: scopeFor(loc.pathname),
            page_label: screen.label,
            screen_data: screenData,
          });
          const ids: number[] = r.data?.proposed_action_ids || [];
          const reply =
            r.data?.text ||
            (ids.length
              ? "I've proposed the change(s) below — review and approve to run."
              : "(no reply)");
          setMessages((m) => [...m, { role: "assistant", content: reply }]);
          if (ids.length) {
            const fetched = await Promise.all(
              ids.map((id) =>
                api
                  .get(`/api/v1/ai/actions/${id}`)
                  .then((x) => x.data as ProposedAction)
                  .catch(() => null),
              ),
            );
            setActions((prev) => [...prev, ...fetched.filter((a): a is ProposedAction => !!a)]);
          }
        } else {
          const r = await api.post("/api/v1/ai/assist", {
            route: loc.pathname,
            page_label: screen.label,
            messages: next,
            screen_data: screenData,
            // Needed for two things the assistant could not do without it:
            // recall what this customer's technicians have taught it, and
            // attribute the spend to the right tenant.
            customer_id: currentCustomerId ?? null,
          });
          setMessages((m) => [...m, { role: "assistant", content: r.data?.reply || "(no reply)" }]);
          // Surface anything it chose to remember. Silent retention is the part
          // people object to when they find out about it later.
          setLearned(r.data?.learned || []);
        }
      } catch (e: any) {
        setErr(e.response?.data?.detail || "The assistant couldn't respond.");
      } finally {
        setBusy(false);
      }
    },
    [busy, messages, loc.pathname, screen.label, canAct, currentCustomerId],
  );

  const patchAction = (id: number, u: Partial<ProposedAction>) =>
    setActions((prev) => prev.map((a) => (a.id === id ? { ...a, ...u } : a)));

  if (!aiOn) return null;

  // Screens that already ship their own dedicated, context-specific AI
  // assistant would show TWO assistants if the global one also rendered — a
  // confusing redundancy. Suppress the global card on those routes and let the
  // purpose-built assistant own the screen:
  //   • /devices/:id      → per-machine AIAssistPanel (DeviceDetail)
  //   • /tickets/:id      → per-ticket  AIAssistPanel (TicketDetail)
  //   • /active-directory → IdentityAssistPanel (ADUsers)
  //   • /tools/{dns,dhcp,gpo} → IdentityAssistPanel (Tools*)
  const DEDICATED_ASSISTANT_ROUTES = [
    /^\/devices\/[^/]+/,
    /^\/tickets\/[^/]+/,
    /^\/active-directory/,
    /^\/tools\/(dns|dhcp|gpo)/,
  ];
  if (DEDICATED_ASSISTANT_ROUTES.some((re) => re.test(loc.pathname))) return null;

  // Inline, screen-aware assistant card — same pattern as the Identity & Tools
  // AI panel (IdentityAssistPanel): a collapsed header that lives in the page
  // flow, expanding to a chat. Mounted once at the top of every page by Layout,
  // so it's available everywhere without a floating launcher.
  return (
    <div className="bg-gradient-to-br from-violet-50 to-blue-50 dark:from-violet-950/40 dark:to-blue-950/30 border border-violet-200 dark:border-violet-800/60 rounded-xl p-3 mb-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300 flex items-center gap-1.5">
          ✨ AI Assist
          <span className="text-[11px] font-normal normal-case text-gray-400">· {screen.label}</span>
        </span>
        <span className="flex items-center gap-3">
          {/* Only offered once there is something to clear. The conversation is
              archived rather than destroyed — it may be the only record of how
              a decision got made. */}
          {open && messages.length > 0 && (
            <button
              onClick={clearThread}
              className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
              title="Archive this conversation and start a new one"
            >
              New conversation
            </button>
          )}
          <button
            onClick={() => setOpen((v) => !v)}
            className="text-xs text-violet-600 dark:text-violet-300 hover:underline"
          >
            {open ? "Hide" : "Ask AI about this screen"}
          </button>
        </span>
      </div>

      {open && (
        <div className="mt-2">
          <div ref={scrollRef} className="max-h-[26rem] overflow-y-auto space-y-3 pr-1">
            {messages.length === 0 && (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  I can help with the <strong>{screen.label}</strong> screen. Try:
                </p>
                <div className="space-y-2">
                  {screen.examples.map((ex, i) => (
                    <button
                      key={i}
                      onClick={() => send(ex)}
                      className="block w-full text-left text-sm bg-white/70 dark:bg-indigo-900/20 text-indigo-800 dark:text-indigo-200 border border-indigo-100 dark:border-indigo-800/50 rounded-lg px-3 py-2 hover:bg-white dark:hover:bg-indigo-900/40"
                    >
                      {ex}
                    </button>
                  ))}
                  <button
                    onClick={() => send(suggestPrompt(screen.label))}
                    className="block w-full text-left text-sm font-medium bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200 border border-violet-200 dark:border-violet-800/60 rounded-lg px-3 py-2 hover:bg-violet-200 dark:hover:bg-violet-900/60"
                  >
                    💡 Suggest what I can do on this screen
                  </button>
                  {resumable && resumable.length > 0 && (
                    <button
                      onClick={() => {
                        setMessages(resumable);
                        setResumable(null);
                      }}
                      className="block w-full text-left text-xs text-gray-500 dark:text-gray-400 underline decoration-dotted px-3 py-1 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      Resume my previous conversation ({resumable.length} messages)
                    </button>
                  )}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`text-sm rounded-lg px-3 py-2 whitespace-pre-wrap break-words ${
                  m.role === "user"
                    ? "bg-indigo-600 text-white ml-8"
                    : "bg-white dark:bg-gray-700/60 text-gray-800 dark:text-gray-100 mr-4"
                }`}
              >
                {m.content}
              </div>
            ))}
            {actions.map((a) => (
              <AssistActionCard key={a.id} action={a} onChange={(u) => patchAction(a.id, u)} />
            ))}
            {learned.length > 0 && (
              <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/70 dark:bg-emerald-500/10 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 font-semibold mb-1">
                  Remembered for next time
                </div>
                <ul className="text-xs text-emerald-900 dark:text-emerald-200 space-y-0.5">
                  {learned.map((f, i) => (
                    <li key={i}>&middot; {f}</li>
                  ))}
                </ul>
                <a
                  href="/ai-settings"
                  className="text-[10px] text-emerald-700 dark:text-emerald-300 underline"
                >
                  Review or correct in AI Settings
                </a>
              </div>
            )}
            {busy && <div className="text-xs text-gray-400 italic">Thinking…</div>}
          </div>
          {err && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{err}</p>}
          <div className="mt-2 flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
              rows={2}
              placeholder={
                canAct
                  ? `Ask, or tell me to make a change on ${screen.label}…`
                  : `Ask about ${screen.label}…`
              }
              className="flex-1 border dark:border-gray-600 bg-white dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm resize-none"
            />
            <button
              onClick={() => send(suggestPrompt(screen.label))}
              disabled={busy}
              title="Suggest what I can do on this screen"
              className="border border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300 text-sm px-3 py-2 rounded-lg hover:bg-violet-50 dark:hover:bg-violet-900/40 disabled:opacity-50"
            >
              💡
            </button>
            <button
              onClick={() => send(input)}
              disabled={busy || !input.trim()}
              className="bg-indigo-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Compact approve/reject card for an AI-proposed action in the global assist.
 *  Nothing runs until approved; HIGH-risk actions require an approval note. */
function AssistActionCard({
  action,
  onChange,
}: {
  action: ProposedAction;
  onChange: (u: Partial<ProposedAction>) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const approve = async () => {
    let note = "";
    if (action.risk_tier === "high") {
      const n = window.prompt(
        `Approve and run this change?\n\n${action.action_type}\n${JSON.stringify(action.params, null, 2)}\n\nEnter an approval note (required):`,
        "",
      );
      if (!n) return;
      note = n;
    } else if (!window.confirm(`Run this change?\n\n${action.action_type}`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await api.post(`/api/v1/ai/actions/${action.id}/approve-and-execute`, { note });
      onChange({ status: r.data.status });
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/api/v1/ai/actions/${action.id}/reject`, {});
      onChange({ status: r.data.status });
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const done =
    action.status === "succeeded"
      ? { txt: "✓ applied", cls: "text-green-700 dark:text-green-400" }
      : action.status === "failed"
        ? { txt: "✗ failed", cls: "text-red-700 dark:text-red-400" }
        : action.status === "rejected"
          ? { txt: "rejected", cls: "text-gray-500 italic" }
          : null;

  return (
    <div className="mr-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 rounded-lg p-2 text-xs">
      <div className="flex items-center gap-1.5 flex-wrap mb-1">
        <span className="text-[9px] uppercase font-bold px-1 py-0.5 rounded bg-amber-200 dark:bg-amber-900/50 text-amber-900 dark:text-amber-200">
          {action.risk_tier} risk
        </span>
        <code className="text-[10px] bg-white dark:bg-gray-900/60 dark:text-gray-100 px-1 rounded">
          {action.action_type}
        </code>
      </div>
      {/* What it will actually do. The risk chip above says how worried to
          be; this says what to be worried about. */}
      {action.consequence && !done && (
        <div className="text-[11px] text-amber-900 dark:text-amber-200 mb-1 flex gap-1">
          {action.disruptive && <span className="shrink-0">&#9888;</span>}
          <span>
            {action.disruptive && <strong>This may break something. </strong>}
            {action.consequence}
          </span>
        </div>
      )}
      <pre className="text-[10px] bg-white/70 dark:bg-gray-900/50 rounded p-1 overflow-auto text-gray-700 dark:text-gray-200">
        {JSON.stringify(action.params, null, 2)}
      </pre>
      {done ? (
        <div className={`mt-1.5 font-mono ${done.cls}`}>
          {done.txt}
          {action.status === "failed" && action.error_message ? `: ${action.error_message}` : ""}
        </div>
      ) : (
        <div className="mt-1.5 flex items-center gap-2">
          <button
            onClick={approve}
            disabled={busy}
            className="text-[11px] px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold disabled:opacity-50"
          >
            {busy ? "running…" : "▶ Approve & run"}
          </button>
          <button
            onClick={reject}
            disabled={busy}
            className="text-[11px] px-2 py-0.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded"
          >
            Reject
          </button>
          <a href="/ai-actions" className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline ml-auto">
            #{action.id}
          </a>
        </div>
      )}
      {error && <div className="text-[10px] text-red-700 dark:text-red-400 mt-1">{error}</div>}
    </div>
  );
}
